/**
 * Clinic Admin - User Management Page (RBAC Phase 2 — Backend-wired)
 * 
 * Rewired from mock OrganizationRolesContext to real backend APIs:
 *   GET  /api/admin/users
 *   POST /api/admin/users/{id}/roles
 *   POST /api/admin/users/{id}/deactivate
 *   POST /api/admin/users/{id}/activate
 *   POST /api/admin/users/{id}/revoke-clinic
 *   POST /api/admin/users/{id}/reset-password
 *   POST /api/auth/unified/invite
 *   GET  /api/admin/invitations
 *   POST /api/admin/invitations/{token}/cancel
 *   POST /api/admin/invitations/{token}/resend
 */
import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/i18n';
import { useClinicData } from '../../../context/ClinicDataContext';
import { useClinicScope } from '../../../hooks/useClinicScope';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Users, UserPlus, Shield, Mail, Search, MoreVertical,
  Check, X, Clock, AlertCircle, ChevronRight, Edit,
  Trash2, RefreshCw, UserCheck, UserX, Send, Key,
  ChevronLeft, Copy, AlertTriangle, Lock, Activity
} from 'lucide-react';
import { toast } from '../../../hooks/use-toast';
import api from '../../../utils/api';


// Role definitions — CANONICAL TEAM ROLE CATALOGUE (RBAC-A1).
// Labels MUST match the backend workspace catalogue
// (`backend/services/unified_auth_service.py::UnifiedAuthService.WORKSPACE_CATALOG`)
// so that "what you invite as" equals "the workspace label the invitee sees
// after they accept" — no vocabulary drift between Team Invite, Workspace
// Switcher, and Dashboard surfaces.
//
// OF-001D OWNER VALIDATION FAILURE #2 fix:
//   - "Patient Coordinator" label was previously attached to
//     `provider_after_sales_rep` here, which is why invitees ended up in
//     the "After-Sales" workspace — the canonical SSOT.  Renamed to
//     "After-Sales" so display labels are identical end-to-end.
//   - `provider_accountant` was missing — added (canonical route
//     `/clinic/accounting/dashboard`).
//   - `provider_coordinator` keeps canonical "Operations" label.
//   - `provider_doctor` is INTENTIONALLY ABSENT (must be onboarded via
//     Doctor Affiliations).
const CLINIC_ROLES = {
  provider_admin:             { id: 'provider_admin',            nameKey: 'provider.team.roles.clinicAdmin',  color: 'purple',  descKey: 'provider.team.roles.descriptions.clinicAdmin' },
  provider_sales_rep:         { id: 'provider_sales_rep',        nameKey: 'provider.team.roles.sales',        color: 'blue',    descKey: 'provider.team.roles.descriptions.sales' },
  provider_coordinator:       { id: 'provider_coordinator',      nameKey: 'provider.team.roles.operations',   color: 'amber',   descKey: 'provider.team.roles.descriptions.operations' },
  provider_accountant:        { id: 'provider_accountant',       nameKey: 'provider.team.roles.accounting',   color: 'emerald', descKey: 'provider.team.roles.descriptions.accounting' },
  provider_after_sales_rep:   { id: 'provider_after_sales_rep',  nameKey: 'provider.team.roles.afterSales',   color: 'teal',    descKey: 'provider.team.roles.descriptions.afterSales' },
};
const roleName = (rid) => (CLINIC_ROLES[rid] ? i18n.t(CLINIC_ROLES[rid].nameKey) : rid);
const roleDesc = (rid) => (CLINIC_ROLES[rid] ? i18n.t(CLINIC_ROLES[rid].descKey) : '');

const getStatusBadge = (status) => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700 border-green-200';
    case 'deactivated': return 'bg-red-100 text-red-700 border-red-200';
    case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const getInviteStatusBadge = (status) => {
  const s = status?.toLowerCase();
  switch (s) {
    case 'pending': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'accepted': return 'bg-green-100 text-green-700 border-green-200';
    case 'expired': return 'bg-red-100 text-red-700 border-red-200';
    case 'cancelled': case 'revoked': return 'bg-gray-100 text-gray-600 border-gray-200';
    default: return 'bg-gray-100 text-gray-600';
  }
};

// Auth helper
const authHeaders = () => {
  const token = '';
  return { 'Content-Type': 'application/json' };
};

// API helpers — uses imported `api` (axios wrapper)
const userMgmtAPI = {
  async getUsers(clinicId) {
    const res = await api.get(`/admin/users?clinic_id=${clinicId}&limit=100`);
    return res.data;
  },
  async getInvitations(clinicId) {
    const res = await api.get(`/admin/invitations?clinic_id=${clinicId}`);
    return res.data;
  },
  async sendInvite(email, role, clinicId, invitedBy) {
    const res = await api.post('/auth/unified/invite', { email, role, clinic_id: clinicId, invited_by: invitedBy });
    return res.data;
  },
  async addRole(userId, roleId, clinicId) {
    const res = await api.post(`/admin/users/${userId}/roles`, { role_id: roleId, clinic_id: clinicId });
    return res.data;
  },
  async removeRole(userId, roleId, clinicId) {
    const res = await api.delete(`/admin/users/${userId}/roles/${roleId}?clinic_id=${clinicId}`);
    return res.data;
  },
  async changeRole(userId, oldRoleId, newRoleId, clinicId) {
    const res = await api.post(`/admin/users/${userId}/change-role`, { old_role_id: oldRoleId, new_role_id: newRoleId, clinic_id: clinicId });
    return res.data;
  },
  async deactivateUser(userId) {
    const res = await api.post(`/admin/users/${userId}/deactivate`, {});
    return res.data;
  },
  async activateUser(userId) {
    const res = await api.post(`/admin/users/${userId}/activate`);
    return res.data;
  },
  async revokeClinic(userId, clinicId) {
    const res = await api.post(`/admin/users/${userId}/revoke-clinic`, { clinic_id: clinicId });
    return res.data;
  },
  async resetPassword(userId, newPassword) {
    const res = await api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
    return res.data;
  },
  async cancelInvite(token) {
    const res = await api.post(`/admin/invitations/${token}/cancel`);
    return res.data;
  },
  async resendInvite(token) {
    const res = await api.post(`/admin/invitations/${token}/resend`);
    return res.data;
  },
  async getThreadsByAgent(clinicId, agentId) {
    const res = await api.get(`/v2/threads/by-agent?clinic_id=${clinicId}&agent_id=${agentId}`);
    return res.data;
  },
  async reassignSelected(clinicId, fromAgentId, threadIds, toAgentId) {
    const res = await api.post('/v2/threads/reassign-selected', { clinic_id: clinicId, from_agent_id: fromAgentId, thread_ids: threadIds, to_agent_id: toAgentId });
    return res.data;
  },
  async reassignBulk(clinicId, fromAgentId, toAgentId) {
    const res = await api.post('/v2/threads/reassign-bulk', { clinic_id: clinicId, from_agent_id: fromAgentId, to_agent_id: toAgentId });
    return res.data;
  },
  async getAuditLog(limit = 20) {
    const res = await api.get(`/admin/audit-log?limit=${limit}`);
    return res.data;
  },
};

// ── Invite Modal ────────────────────────────────────────────────────
const InviteUserModal = ({ clinicId, clinicName, inviterId, onClose, onInvited }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', role: 'provider_sales_rep' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError(t('provider.team.users.invalidEmail'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await userMgmtAPI.sendInvite(formData.email, formData.role, clinicId, inviterId || 'unknown');
      if (res.success) {
        toast({ title: t('provider.team.users.invitationSentTitle'), description: t('provider.team.users.invitationSentBody', { email: formData.email }) });
        onInvited();
        onClose();
      } else {
        setError(res.error || res.detail || t('provider.team.users.failedSendInvitation'));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="invite-modal" className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t('provider.team.users.inviteModalTitle')}</h2>
              <p className="text-sm text-gray-500">{clinicName}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div data-testid="invite-error" className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}
          <div>
            <Label>{t('provider.team.users.emailLabel')}</Label>
            <Input
              data-testid="invite-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('provider.team.users.emailPlaceholder')}
            />
          </div>
          <div>
            <Label>{t('provider.team.users.roleLabel')}</Label>
            <select
              data-testid="invite-role-select"
              className="w-full border rounded-lg p-2.5"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              {Object.values(CLINIC_ROLES).map(role => (
                <option key={role.id} value={role.id}>{i18n.t(role.nameKey)}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">{roleDesc(formData.role)}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800 font-medium mb-2">{t('provider.team.users.invitationFlow')}</p>
            <div className="flex items-center gap-2 text-xs">
              <Badge className="bg-blue-100 text-blue-700">{t('provider.team.statuses.sent')}</Badge>
              <ChevronRight className="h-3 w-3 text-blue-400" />
              <Badge className="bg-amber-100 text-amber-700">{t('provider.team.statuses.pendingUpper')}</Badge>
              <ChevronRight className="h-3 w-3 text-blue-400" />
              <Badge className="bg-green-100 text-green-700">{t('provider.team.statuses.acceptedUpper')}</Badge>
            </div>
          </div>
        </div>
        <div className="p-4 bg-gray-50 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">{t('provider.team.users.cancel')}</Button>
          <Button data-testid="invite-submit-btn" onClick={handleSubmit} disabled={loading} className="flex-1">
            {loading ? t('provider.team.users.sending') : t('provider.team.users.sendInvitation')}
            <Send className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Invitation Card ─────────────────────────────────────────────────
const InvitationCard = ({ invitation, onCancel, onResend }) => {
  const { t } = useTranslation();
  const roleInfo = CLINIC_ROLES[invitation.role] || { name: invitation.role, color: 'gray' };
  const statusStyle = getInviteStatusBadge(invitation.status);
  const isPending = invitation.status?.toLowerCase() === 'pending';

  const copyInviteLink = () => {
    const link = `${window.location.origin}/#/register?invite=${invitation.token}`;
    navigator.clipboard.writeText(link);
    toast({ title: t('provider.team.users.invLinkCopiedTitle'), description: t('provider.team.users.invLinkCopiedBody') });
  };

  return (
    <Card data-testid={`invitation-card-${invitation.token}`} className="border-l-4 border-l-blue-400">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Mail className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">{invitation.email}</p>
                <Badge className={`text-xs ${statusStyle}`}>{i18n.t('provider.team.statuses.' + (invitation.status?.toLowerCase() || 'pending'), { defaultValue: invitation.status?.toUpperCase() }).toUpperCase()}</Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                <span>Role: {i18n.t(roleInfo.nameKey || 'provider.team.roles.descriptions.clinicAdmin')}</span>
                <span>{t('provider.team.users.invitedLineLabel', { date: formatDateLocale(invitation.created_at) })}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPending && (
              <>
                <Button variant="ghost" size="sm" onClick={copyInviteLink} title={t('provider.team.users.copyInviteLinkTitle')}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => onResend(invitation.token)} className="text-blue-600">
                  <RefreshCw className="h-4 w-4 mr-1" /> {t('provider.team.users.resend')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onCancel(invitation.token)} className="text-red-600 hover:bg-red-50">
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
        {invitation.status?.toLowerCase() === 'expired' && (
          <div className="mt-3 p-2 bg-red-50 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>{t('provider.team.users.expiredWarning')}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── User Card ───────────────────────────────────────────────────────
const UserCard = ({ user, clinicId, onAction }) => {
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState(false);
  const isActive = user.status === 'active';
  const clinicRoles = (user.role_assignments || [])
    .filter(ra => ra.scope?.type === 'clinic' && ra.scope?.id === clinicId)
    .map(ra => ra.role_id);
  const primaryRole = clinicRoles[0] || 'unknown';
  const roleInfo = CLINIC_ROLES[primaryRole] || { name: primaryRole, color: 'gray' };

  return (
    <Card data-testid={`user-card-${user.user_id}`} className={!isActive ? 'opacity-60' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
              {(user.name || user.email || '?')[0].toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-semibold">{user.name || user.email}</h4>
                <Badge className={`text-xs ${getStatusBadge(user.status)}`}>{user.status}</Badge>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{user.email}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('provider.team.users.userIdLabel', { id: user.user_id })}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex flex-wrap gap-1">
              {clinicRoles.map(r => (
                <Badge key={r} className={`bg-${CLINIC_ROLES[r]?.color || 'gray'}-100 text-${CLINIC_ROLES[r]?.color || 'gray'}-700 text-xs`}>
                  {roleName(r) || r}
                </Badge>
              ))}
            </div>
            <div className="relative">
              <Button variant="ghost" size="icon" onClick={() => setShowActions(!showActions)}>
                <MoreVertical className="h-4 w-4" />
              </Button>
              {showActions && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 z-10 min-w-[180px]">
                  <button
                    data-testid={`action-change-role-${user.user_id}`}
                    onClick={() => { onAction('changeRole', user); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" /> {t('provider.team.users.actionChangeRole')}
                  </button>
                  <button
                    data-testid={`action-reset-pwd-${user.user_id}`}
                    onClick={() => { onAction('resetPassword', user); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Lock className="h-4 w-4" /> {t('provider.team.users.actionResetPwd')}
                  </button>
                  {isActive ? (
                    <button
                      data-testid={`action-deactivate-${user.user_id}`}
                      onClick={() => { onAction('deactivate', user); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-amber-600"
                    >
                      <UserX className="h-4 w-4" /> {t('provider.team.users.actionDeactivate')}
                    </button>
                  ) : (
                    <button
                      data-testid={`action-activate-${user.user_id}`}
                      onClick={() => { onAction('activate', user); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-600"
                    >
                      <UserCheck className="h-4 w-4" /> {t('provider.team.users.actionReactivate')}
                    </button>
                  )}
                  <button
                    data-testid={`action-transfer-${user.user_id}`}
                    onClick={() => { onAction('transferPatients', user); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-teal-600"
                  >
                    <RefreshCw className="h-4 w-4" /> {t('provider.team.users.actionTransfer')}
                  </button>
                  <button
                    data-testid={`action-revoke-${user.user_id}`}
                    onClick={() => { onAction('revokeClinic', user); setShowActions(false); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                  >
                    <Trash2 className="h-4 w-4" /> {t('provider.team.users.actionRemoveClinic')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Change Role Modal ───────────────────────────────────────────────
const ChangeRoleModal = ({ user, clinicId, onClose, onSaved }) => {
  const { t } = useTranslation();
  const clinicRoles = (user.role_assignments || [])
    .filter(ra => ra.scope?.type === 'clinic' && ra.scope?.id === clinicId);
  const currentRole = clinicRoles[0]?.role_id || '';
  const [newRole, setNewRole] = useState(currentRole);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (newRole === currentRole) { onClose(); return; }
    setLoading(true);
    try {
      // Use atomic change-role to preserve clinic membership
      await userMgmtAPI.changeRole(user.user_id, currentRole, newRole, clinicId);
      toast({ title: t('provider.team.users.roleUpdatedTitle'), description: t('provider.team.users.roleUpdatedBody', { role: roleName(newRole) || newRole }) });
      // Close first, then refresh list to avoid stale modal state
      onClose();
      setTimeout(() => onSaved(), 200);
    } catch (e) {
      toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="change-role-modal" className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-sm w-full">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t('provider.team.users.changeRoleTitle')}</h2>
              <p className="text-sm text-gray-500">{user.name || user.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
          </div>
        </div>
        <div className="p-6">
          <Label>{t('provider.team.users.newRoleLabel')}</Label>
          <select
            data-testid="change-role-select"
            className="w-full border rounded-lg p-2.5 mt-2"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          >
            {Object.values(CLINIC_ROLES).map(role => (
              <option key={role.id} value={role.id}>{i18n.t(role.nameKey)}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-2">{roleDesc(newRole)}</p>
        </div>
        <div className="p-4 bg-gray-50 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            <ChevronLeft className="h-4 w-4 mr-1" /> {t('provider.team.users.cancel')}
          </Button>
          <Button data-testid="change-role-save-btn" onClick={handleSave} disabled={loading} className="flex-1">
            <Check className="h-4 w-4 mr-1" /> {loading ? t('provider.team.users.saving') : t('provider.team.users.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Reset Password Modal — REDESIGNED for safety ────────────────────
// P0-2 FIX: Admin can no longer directly set passwords.
// Instead shows confirmation that a password reset will be triggered.
const ResetPasswordModal = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendReset = async () => {
    setLoading(true);
    try {
      await userMgmtAPI.resetPassword(user.user_id, null);
      setSent(true);
      toast({ title: t('provider.team.users.resetInitiatedTitle'), description: t('provider.team.users.resetInitiatedBody', { email: user.email }) });
    } catch (e) {
      toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-modal" className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-sm w-full">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t('provider.team.users.resetPwdTitle')}</h2>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
          </div>
          <p className="text-sm text-gray-500">{user.name || user.email}</p>
        </div>
        <div className="p-6 space-y-3">
          {sent ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
              {t('provider.team.users.resetSentBody')}
            </div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                {t('provider.team.users.resetWarnBody')}
              </div>
              <p className="text-sm text-gray-600">
                {t('provider.team.users.resetSecurityNote')}
              </p>
            </>
          )}
        </div>
        <div className="p-4 bg-gray-50 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {sent ? t('provider.team.users.done') : t('provider.team.users.cancel')}
          </Button>
          {!sent && (
            <Button data-testid="reset-password-submit-btn" onClick={handleSendReset} disabled={loading} className="flex-1 bg-amber-600 hover:bg-amber-700">
              {loading ? t('provider.team.users.processing') : t('provider.team.users.initiateReset')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Transfer Patients Modal ─────────────────────────────────────────
const TransferPatientsModal = ({ user, clinicId, staffList, onClose, onComplete }) => {
  const { t } = useTranslation();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetAgentId, setTargetAgentId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Find the clinic_agents entry for this user
  const agentId = user._agentId; // populated by caller

  useEffect(() => {
    if (!agentId) { setLoading(false); return; }
    const load = async () => {
      try {
        const res = await userMgmtAPI.getThreadsByAgent(clinicId, agentId);
        setThreads(res.threads || []);
      } catch {
        toast({ title: t('provider.team.users.errorTitle'), description: t('provider.team.users.loadThreadsFailed'), variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [agentId, clinicId]);

  const filteredThreads = threads.filter(t =>
    (t.patient_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.id || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredThreads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredThreads.map(t => t.id)));
    }
  };

  const handleTransferSelected = async (toUnassigned = false) => {
    if (selectedIds.size === 0) {
      toast({ title: t('provider.team.users.noSelectionTitle'), description: t('provider.team.users.noSelectionBody'), variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await userMgmtAPI.reassignSelected(
        clinicId,
        agentId,
        [...selectedIds],
        toUnassigned ? null : (targetAgentId || null),
      );
      toast({
        title: t('provider.team.users.threadsTransferredTitle'),
        description: t('provider.team.users.threadsTransferredBody', { count: res.threads_reassigned, target: res.to_agent_name || t('provider.team.users.unassignedQueueLabel') }),
      });
      // Remove transferred threads from local list
      setThreads(prev => prev.filter(t => !selectedIds.has(t.id)));
      setSelectedIds(new Set());
      if (threads.length - selectedIds.size === 0) {
        onComplete();
        onClose();
      }
    } catch (e) {
      toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransferAll = async () => {
    if (!targetAgentId && !window.confirm(t('provider.team.users.confirmMoveAllUnassigned'))) return;
    setSubmitting(true);
    try {
      const res = await userMgmtAPI.reassignBulk(clinicId, agentId, targetAgentId || null);
      toast({
        title: t('provider.team.users.allTransferredTitle'),
        description: t('provider.team.users.allTransferredBody', { count: res.threads_reassigned }),
      });
      onComplete();
      onClose();
    } catch (e) {
      toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Other staff (excluding the current user) for target selection
  const otherStaff = staffList.filter(s => s.user_id !== user.user_id && s.status === 'active');

  return (
    <div data-testid="transfer-patients-modal" className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t('provider.team.users.transferPatientsTitle')}</h2>
              <p className="text-sm text-gray-500">{user.name || user.email} — {t('provider.team.users.transferThreadsAssigned', { count: threads.length })}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
          </div>
        </div>

        {/* Target selector */}
        <div className="px-5 py-3 bg-gray-50 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Label className="text-sm whitespace-nowrap">{t('provider.team.users.transferToLabel')}</Label>
            <select
              data-testid="transfer-target-select"
              className="flex-1 border rounded-lg p-2 text-sm"
              value={targetAgentId}
              onChange={(e) => setTargetAgentId(e.target.value)}
            >
              <option value="">{t('provider.team.users.unassignedQueue')}</option>
              {otherStaff.map(s => (
                <option key={s.user_id} value={s._agentId || s.user_id}>
                  {s.name || s.email} ({(s.role_assignments || []).find(ra => ra.scope?.id === clinicId)?.role_id || 'staff'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search + Select All */}
        <div className="px-5 py-3 border-b flex-shrink-0 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="transfer-search-input"
              placeholder={t('provider.team.users.searchPatientsPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              data-testid="transfer-select-all"
              type="checkbox"
              checked={filteredThreads.length > 0 && selectedIds.size === filteredThreads.length}
              onChange={toggleAll}
              className="w-4 h-4 rounded border-gray-300"
            />
            {t('provider.team.users.selectAll', { count: filteredThreads.length })}
          </label>
        </div>

        {/* Thread list */}
        <div className="flex-1 overflow-y-auto px-5 py-2 min-h-0">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
              <p className="mt-2 text-sm">{t('provider.team.users.loadingThreads')}</p>
            </div>
          ) : threads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-2">{t('provider.team.users.noThreadsAssignedTitle')}</p>
              <Button onClick={onClose} className="mt-4" variant="outline">{t('provider.team.users.close')}</Button>
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Search className="h-6 w-6 mx-auto opacity-50" />
              <p className="mt-2 text-sm">{t('provider.team.users.noThreadsMatch', { q: searchQuery })}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredThreads.map(t => (
                <label
                  key={t.id}
                  data-testid={`transfer-thread-${t.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedIds.has(t.id) ? 'bg-teal-50 border border-teal-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(t.id)}
                    onChange={() => toggleSelect(t.id)}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{t.patient_name}</span>
                      <Badge className="text-xs bg-gray-100 text-gray-600">{t.thread_type || 'lead'}</Badge>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 flex gap-3">
                      <span>{t.lead_source || 'N/A'}</span>
                      <span>{t.updated_at ? formatDateLocale(t.updated_at) : ''}</span>
                    </div>
                  </div>
                  <Badge className={`text-xs ${t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {t.status}
                  </Badge>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {threads.length > 0 && (
          <div className="p-4 bg-gray-50 border-t flex-shrink-0">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {t('provider.team.users.selectedCount', { selected: selectedIds.size, total: filteredThreads.length })}
              </p>
              <div className="flex gap-2">
                <Button
                  data-testid="transfer-to-unassigned-btn"
                  variant="outline"
                  size="sm"
                  disabled={selectedIds.size === 0 || submitting}
                  onClick={() => handleTransferSelected(true)}
                >
                  {t('provider.team.users.moveToUnassigned')}
                </Button>
                <Button
                  data-testid="transfer-selected-btn"
                  size="sm"
                  disabled={selectedIds.size === 0 || submitting}
                  onClick={() => handleTransferSelected(false)}
                >
                  {submitting ? t('provider.team.users.transferring') : t('provider.team.users.transferSelected', { count: selectedIds.size })}
                </Button>
                <Button
                  data-testid="transfer-all-btn"
                  variant="secondary"
                  size="sm"
                  disabled={submitting}
                  onClick={handleTransferAll}
                >
                  {t('provider.team.users.transferAll', { count: threads.length })}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Action Type → Human-readable message ────────────────────────────
const ACTION_LABELS = {
  user_updated:                  { labelKey: 'provider.team.users.actionUpdatedUser',           icon: Edit,       color: 'text-blue-600'   },
  user_deactivated:              { labelKey: 'provider.team.users.actionDeactivatedUser',       icon: UserX,      color: 'text-red-600'    },
  user_activated:                { labelKey: 'provider.team.users.actionActivatedUser',         icon: UserCheck,  color: 'text-green-600'  },
  role_assigned:                 { labelKey: 'provider.team.users.actionAssignedRole',          icon: Shield,     color: 'text-purple-600' },
  role_removed:                  { labelKey: 'provider.team.users.actionRemovedRole',           icon: Trash2,     color: 'text-amber-600'  },
  clinic_membership_revoked:     { labelKey: 'provider.team.users.actionRevokedClinic',         icon: X,          color: 'text-red-600'    },
  password_reset:                { labelKey: 'provider.team.users.actionResetPassword',         icon: Key,        color: 'text-amber-600'  },
  'thread.reassignment.bulk':    { labelKey: 'provider.team.users.actionBulkReassignedThreads', icon: RefreshCw, color: 'text-teal-600' },
  'thread.reassignment.selective': { labelKey: 'provider.team.users.actionReassignedThreads',     icon: RefreshCw,  color: 'text-teal-600'   },
  threads_bulk_reassigned:       { label: 'Bulk reassigned threads', icon: RefreshCw, color: 'text-teal-600' },
  invitation_cancelled:          { labelKey: 'provider.team.users.actionCancelledInvitation',   icon: X,          color: 'text-gray-600'   },
  invitation_resent:             { labelKey: 'provider.team.users.actionResentInvitation',      icon: Send,       color: 'text-blue-600'   },
};

const formatActionMessage = (event) => {
  const info = ACTION_LABELS[event.action_type] || { labelKey: null, label: event.action_type, icon: Activity, color: 'text-gray-600' };
  const meta = event.metadata || {};

  let detail = '';
  if (event.action_type === 'role_assigned' && meta.role_id) {
    detail = roleName(meta.role_id) || meta.role_id;
  } else if (event.action_type === 'role_removed' && meta.role_id) {
    detail = roleName(meta.role_id) || meta.role_id;
  } else if (event.action_type === 'user_updated' && meta.fields) {
    detail = meta.fields.filter(f => f !== 'updated_at').join(', ');
  } else if (event.action_type === 'clinic_membership_revoked' && meta.roles_removed) {
    detail = meta.roles_removed.map(r => roleName(r) || r).join(', ');
  } else if ((event.action_type === 'thread.reassignment.bulk' || event.action_type === 'threads_bulk_reassigned') && meta.thread_count) {
    detail = i18n.t('provider.team.users.threadCountLabel', { n: meta.thread_count });
  } else if (event.action_type === 'thread.reassignment.selective' && meta.thread_count) {
    detail = `${meta.thread_count} thread(s)`;
  } else if ((event.action_type === 'invitation_cancelled' || event.action_type === 'invitation_resent') && meta.invite_email) {
    detail = meta.invite_email;
  }

  return { ...info, label: info.labelKey ? i18n.t(info.labelKey) : info.label, detail };
};

const timeAgo = (timestamp) => {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return i18n.t('provider.team.users.timeJustNow');
  if (mins < 60) return i18n.t('provider.team.users.timeMinutes', { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return i18n.t('provider.team.users.timeHours', { n: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return i18n.t('provider.team.users.timeDays', { n: days });
  return formatDateLocale(timestamp);
};

// ── Recent Activity Panel ───────────────────────────────────────────
const RecentActivityPanel = ({ refreshKey }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userMgmtAPI.getAuditLog(20);
      setEvents(res.events || []);
    } catch {
      // silent fail — panel is supplementary
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAuditLog(); }, [loadAuditLog, refreshKey]);

  return (
    <Card data-testid="recent-activity-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4 text-teal-600" /> {t('provider.team.users.recentActivity')}
          </CardTitle>
          <Button
            data-testid="refresh-activity-btn"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={loadAuditLog}
            disabled={loading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && events.length === 0 ? (
          <div className="flex items-center justify-center py-6 text-gray-400">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            <span className="text-xs">{t('provider.team.users.loadingActivity')}</span>
          </div>
        ) : events.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">{t('provider.team.users.noRecentActivity')}</p>
        ) : (
          <div className="space-y-0 max-h-[420px] overflow-y-auto -mx-1 px-1">
            {events.map((ev) => {
              const { label, icon: Icon, color, detail } = formatActionMessage(ev);
              return (
                <div
                  key={ev.audit_id}
                  data-testid={`activity-event-${ev.audit_id}`}
                  className="flex items-start gap-2.5 py-2.5 border-b border-gray-50 last:border-0"
                >
                  <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs leading-relaxed">
                      <span className="font-medium">{ev.actor_name || t('provider.team.users.systemActor')}</span>
                      {' '}<span className="text-gray-500">{label.toLowerCase()}</span>
                      {ev.target_name && (
                        <>{' '}<span className="font-medium">{ev.target_name}</span></>
                      )}
                    </p>
                    {detail && (
                      <p className="text-[11px] text-gray-400 mt-0.5 truncate">{detail}</p>
                    )}
                    <p className="text-[11px] text-gray-300 mt-0.5">{timeAgo(ev.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main Component ──────────────────────────────────────────────────
const UserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();

  const clinicId = rbacClinicId || clinicUser?.clinicId;
  const clinicName = clinicUser?.clinicName || i18n.t('provider.team.users.clinicFallback');

  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('members');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState(null); // 'changeRole' | 'resetPassword' | 'transferPatients'
  const [clinicAgents, setClinicAgents] = useState([]);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        userMgmtAPI.getUsers(clinicId),
        userMgmtAPI.getInvitations(clinicId),
      ]);
      setUsers(usersRes.users || []);
      setInvitations(invitesRes.invitations || []);

      // Load clinic_agents to map user_id → agent_id
      const agentsRes = await api.get(`/clinic/agents?clinic_id=${clinicId}`);
      if (agentsRes.ok) {
        const agentsData = agentsRes.data;
        setClinicAgents(agentsData.agents || agentsData || []);
      }
    } catch (e) {
      toast({ title: t('provider.team.users.errorTitle'), description: t('provider.team.users.loadFailed'), variant: 'destructive' });
    } finally {
      setLoading(false);
      setActivityRefreshKey(k => k + 1);
    }
  }, [clinicId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredUsers = users.filter(u =>
    (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingInvitations = invitations.filter(i => i.status?.toLowerCase() === 'pending');
  const activeUsers = users.filter(u => u.status === 'active');

  // Role count
  const roleCount = {};
  users.forEach(u => {
    (u.role_assignments || []).forEach(ra => {
      if (ra.scope?.type === 'clinic' && ra.scope?.id === clinicId) {
        roleCount[ra.role_id] = (roleCount[ra.role_id] || 0) + 1;
      }
    });
  });

  // Helper: find clinic_agent.id for a given user
  const getAgentIdForUser = (userId) => {
    const agent = clinicAgents.find(a => a.user_id === userId && a.clinic_id === clinicId);
    return agent?.id || null;
  };

  // Attach _agentId to users for the transfer modal
  const usersWithAgentIds = users.map(u => ({ ...u, _agentId: getAgentIdForUser(u.user_id) }));

  const handleAction = async (action, user) => {
    const enrichedUser = { ...user, _agentId: getAgentIdForUser(user.user_id) };
    switch (action) {
      case 'changeRole':
        setSelectedUser(enrichedUser);
        setModalType('changeRole');
        break;
      case 'resetPassword':
        setSelectedUser(enrichedUser);
        setModalType('resetPassword');
        break;
      case 'deactivate':
        if (window.confirm(t('provider.team.users.confirmDeactivate', { name: user.name || user.email }))) {
          try {
            await userMgmtAPI.deactivateUser(user.user_id);
            toast({ title: t('provider.team.users.deactivatedTitle'), description: t('provider.team.users.deactivatedBody', { name: user.name || user.email }) });
            loadData();
          } catch (e) {
            toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
          }
        }
        break;
      case 'activate':
        try {
          await userMgmtAPI.activateUser(user.user_id);
          toast({ title: t('provider.team.users.reactivatedTitle'), description: t('provider.team.users.reactivatedBody', { name: user.name || user.email }) });
          loadData();
        } catch (e) {
          toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
        }
        break;
      case 'transferPatients':
        setSelectedUser(enrichedUser);
        setModalType('transferPatients');
        break;
      case 'revokeClinic':
        // Show transfer modal first so admin can decide what to do with threads
        setSelectedUser(enrichedUser);
        setModalType('transferPatients');
        break;
      default:
        break;
    }
  };

  const handleCancelInvitation = async (token) => {
    if (!window.confirm(t('provider.team.users.confirmCancelInvitation'))) return;
    try {
      await userMgmtAPI.cancelInvite(token);
      toast({ title: t('provider.team.users.cancelledTitle'), description: t('provider.team.users.cancelledBody') });
      loadData();
    } catch (e) {
      toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
    }
  };

  const handleResendInvitation = async (token) => {
    try {
      await userMgmtAPI.resendInvite(token);
      toast({ title: t('provider.team.users.resentTitle'), description: t('provider.team.users.resentBody') });
      loadData();
    } catch (e) {
      toast({ title: t('provider.team.users.errorTitle'), description: e.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-teal-600 mx-auto" />
          <p className="mt-3 text-gray-500">{t('provider.team.users.loadingTeam')}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="user-management-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-800 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Button variant="ghost" onClick={() => navigate('/clinic/admin')} className="text-teal-200 hover:text-white mb-2">
                <ChevronLeft className="h-4 w-4 mr-1" /> {t('provider.team.users.backToDashboard')}
              </Button>
              <h1 className="text-2xl font-bold">{t('provider.team.users.pageTitle')}</h1>
              <p className="text-teal-200 text-sm">{clinicName}</p>
            </div>
            <Button data-testid="invite-user-btn" onClick={() => setShowInviteModal(true)} className="bg-white text-teal-700 hover:bg-teal-50">
              <UserPlus className="h-4 w-4 mr-2" /> {t('provider.team.users.inviteUser')}
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-white shadow-md">
            <CardContent className="p-4 text-center">
              <p data-testid="stat-total-users" className="text-3xl font-bold text-gray-900">{users.length}</p>
              <p className="text-sm text-gray-500">{t('provider.team.users.statTotal')}</p>
            </CardContent>
          </Card>
          {Object.entries(CLINIC_ROLES).slice(0, 4).map(([roleId, role]) => (
            <Card key={roleId} className="bg-white shadow-md">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-gray-900">{roleCount[roleId] || 0}</p>
                <p className="text-sm text-gray-500">{i18n.t(role.nameKey)}</p>
              </CardContent>
            </Card>
          ))}
          <Card className="bg-amber-50 shadow-md border-amber-200">
            <CardContent className="p-4 text-center">
              <p data-testid="stat-pending-invites" className="text-3xl font-bold text-amber-600">{pendingInvitations.length}</p>
              <p className="text-sm text-amber-700">{t('provider.team.users.statPending')}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger data-testid="tab-members" value="members" className="gap-2">
              <Users className="h-4 w-4" /> {t('provider.team.users.tabMembers', { count: users.length })}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-invitations" value="invitations" className="gap-2">
              <Mail className="h-4 w-4" /> {t('provider.team.users.tabInvitations', { count: invitations.length })}
              {pendingInvitations.length > 0 && (
                <Badge className="ml-1 bg-amber-500 text-white text-xs">{pendingInvitations.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-teal-600" /> {t('provider.team.users.cardMembersTitle', { count: filteredUsers.length })}
                      </CardTitle>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          data-testid="search-users-input"
                          placeholder={t('provider.team.users.searchMembersPlaceholder')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 w-48"
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {filteredUsers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>{t('provider.team.users.emptyMembersTitle')}</p>
                        <Button onClick={() => setShowInviteModal(true)} className="mt-4">
                          <UserPlus className="h-4 w-4 mr-2" /> {t('provider.team.users.inviteFirstMember')}
                        </Button>
                      </div>
                    ) : (
                      filteredUsers.map(user => (
                        <UserCard key={user.user_id} user={user} clinicId={clinicId} onAction={handleAction} />
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <RecentActivityPanel refreshKey={activityRefreshKey} />
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Shield className="h-4 w-4 text-teal-600" /> {t('provider.team.users.teamSummary')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{t('provider.team.users.summaryActive')}</span>
                        <span className="font-medium">{activeUsers.length}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{t('provider.team.users.summaryPending')}</span>
                        <span className="font-medium text-amber-600">{pendingInvitations.length}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Key className="h-4 w-4 text-purple-600" /> {t('provider.team.users.rolePermissions')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.values(CLINIC_ROLES).map(role => (
                        <div key={role.id} className="border-b pb-2 last:border-0">
                          <Badge className={`bg-${role.color}-100 text-${role.color}-700 text-xs`}>{i18n.t(role.nameKey)}</Badge>
                          <p className="text-xs text-gray-500 mt-1">{i18n.t(role.descKey)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invitations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-teal-600" /> {t('provider.team.users.allInvitationsTitle')}
                </CardTitle>
                <CardDescription>{t('provider.team.users.allInvitationsDescription')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {invitations.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t('provider.team.users.noInvitationsYet')}</p>
                    <Button onClick={() => setShowInviteModal(true)} className="mt-4">
                      <UserPlus className="h-4 w-4 mr-2" /> {t('provider.team.users.sendFirstInvitation')}
                    </Button>
                  </div>
                ) : (
                  ['pending', 'accepted', 'expired', 'cancelled', 'revoked'].map(status => {
                    const filtered = invitations.filter(i => i.status?.toLowerCase() === status);
                    if (!filtered.length) return null;
                    return (
                      <div key={status} className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                          {status} ({filtered.length})
                        </h3>
                        {filtered.map(inv => (
                          <InvitationCard
                            key={inv.token}
                            invitation={inv}
                            onCancel={handleCancelInvitation}
                            onResend={handleResendInvitation}
                          />
                        ))}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      {showInviteModal && (
        <InviteUserModal
          clinicId={clinicId}
          clinicName={clinicName}
          inviterId={clinicUser?.id || clinicUser?.userId || 'system'}
          onClose={() => setShowInviteModal(false)}
          onInvited={loadData}
        />
      )}
      {selectedUser && modalType === 'changeRole' && (
        <ChangeRoleModal
          user={selectedUser}
          clinicId={clinicId}
          onClose={() => { setSelectedUser(null); setModalType(null); }}
          onSaved={loadData}
        />
      )}
      {selectedUser && modalType === 'resetPassword' && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() => { setSelectedUser(null); setModalType(null); }}
        />
      )}
      {selectedUser && modalType === 'transferPatients' && (
        <TransferPatientsModal
          user={selectedUser}
          clinicId={clinicId}
          staffList={usersWithAgentIds}
          onClose={() => { setSelectedUser(null); setModalType(null); }}
          onComplete={loadData}
        />
      )}
    </div>
  );
};

export default UserManagement;
